import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Flex,
  Divider,
  Skeleton,
} from '@chakra-ui/react';
import React from 'react';

import { useGetOrganizationImplementationsDetailsQuery } from '../requests/implementations.gql.generated';
import { BankAccountsTable } from './bankAccounts/BankAccountsTable';
import {
  CreditFacilitiesTable,
  CreditFacility,
} from './creditFacilities/CreditFacilitiesTable';
import { LoanProductsTable } from './loanProducts/LoanProductsTable';
import { useVisibleOrganizationsQuery } from '~/core/routes/settings/implementations/implementations.gql.generated';
import { LabeledDetail } from '~/packages/common-frontend/components/LabeledDetail';
import { Typography } from '~/packages/common-frontend/components/Typography';

// Types
type Implementation = NonNullable<
  ReturnType<typeof useGetOrganizationImplementationsDetailsQuery>['data']
>['organization']['implementations'][number];

export interface ImplementationDetailsProps {
  organizationId: string;
  implementationId: string;
  implementationRoutePrefix: string; // e.g., `/organizations/{orgId}/implementations/`
  // Render props for dashboard-specific components
  renderActionButtons?: (implementation: Implementation) => React.ReactNode;
  // Credit facilities table props
  renderCreditFacilitiesRowActions?: (
    implementation: Implementation,
    creditFacility: CreditFacility,
  ) => React.ReactNode;
}

// Exports
export const ImplementationDetails = ({
  organizationId,
  implementationId,
  implementationRoutePrefix,
  renderActionButtons,
}: ImplementationDetailsProps) => {
  const {
    data,
    error,
    loading,
  } = useGetOrganizationImplementationsDetailsQuery({
    variables: { organizationId },
  });

  const visibleOrganizationsQuery = useVisibleOrganizationsQuery({
    variables: { organizationId },
  });

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertDescription>Failed to load implementations</AlertDescription>
      </Alert>
    );
  }
  if (loading || visibleOrganizationsQuery.loading) {
    return <Skeleton />;
  }

  const currentImplementation = data?.organization.implementations.find(
    (implementation: Implementation) => implementation.id === implementationId,
  );

  if (!currentImplementation) {
    return null;
  }

  const visibleOrganizations =
    visibleOrganizationsQuery.data?.organization.visibleOrganizations ?? [];

  return (
    <Box>
      <Box>
        <Flex justifyContent="space-between" padding="25px 0px">
          <Typography variant="page-title">
            {currentImplementation.name}
          </Typography>
          {renderActionButtons && (
            <Flex gridGap="12px">
              {renderActionButtons(currentImplementation)}
            </Flex>
          )}
        </Flex>

        <Flex gridGap="130px" padding="30px 0px">
          <LabeledDetail label="Slug">
            {currentImplementation.slug}
          </LabeledDetail>
          <LabeledDetail label="ID">{currentImplementation.id}</LabeledDetail>
        </Flex>
        <Divider />
        <Flex gridGap="130px" padding="30px 0px">
          <LabeledDetail label="Borrower Organization">
            {currentImplementation.borrowerOrganization.name}
          </LabeledDetail>
        </Flex>
        <Divider />
      </Box>
      <LoanProductsTable implementation={currentImplementation} />
      <CreditFacilitiesTable
        implementation={currentImplementation}
        implementationRoutePrefix={implementationRoutePrefix}
        organizations={visibleOrganizations}
      />
      <BankAccountsTable
        implementation={currentImplementation}
        organizations={visibleOrganizations}
      />
    </Box>
  );
};
